import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  AuthHttpService,
  isTerminalRefreshCredentialError,
} from '@core/api/auth-http.service';
import { AuthService } from '@core/services/auth.service';
import { REFRESH_SESSION_MARKER } from '@core/services/auth.service';
import { getTestModePreviewHeaders } from '@core/services/test-mode-lifecycle';
import { PERSONAL_OWNER_SCOPE } from './owner-scope.context';
import { environment } from 'src/environments/environment';
import { Subject, catchError, switchMap, take, tap, throwError } from 'rxjs';

const API_BASE = `${environment.apiUrl}/api/`;

/** Prevents multiple simultaneous refresh attempts when several requests get 401 at once. */
let refreshing = false;
const refreshDone$ = new Subject<void>();

/**
 * Prefixes relative API paths with `{apiUrl}/api/`, attaches Bearer token + org header,
 * injects idempotency keys on write requests, refreshes on 401, and reloads session
 * after invitation status changes (mirrors legacy Angular AuthInterceptor).
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Leave absolute URLs alone (i18n assets, etc.)
  if (/^https?:\/\//i.test(req.url) || req.url.startsWith('/assets/')) {
    return next(req);
  }

  const authService = inject(AuthService);
  const authHttp = inject(AuthHttpService);
  const router = inject(Router);

  const apiReq = attachAuth(req);

  return next(apiReq).pipe(
    tap((event) => {
      if (
        event instanceof HttpResponse &&
        event.status === 200 &&
        apiReq.url.includes('OrganizationUser/invitationStatus')
      ) {
        authService.refreshSession().subscribe();
      }
    }),
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 401 &&
        localStorage.getItem(REFRESH_SESSION_MARKER) === '1' &&
        !apiReq.url.includes('auth/refresh-token') &&
        !apiReq.url.includes('auth/login') &&
        !apiReq.url.includes('auth/logout') &&
        !apiReq.url.includes('auth/confirm-email') &&
        !apiReq.url.includes('auth/register')
      ) {
        return handle401(apiReq, next, authHttp, router);
      }
      return throwError(() => error);
    }),
  );
};

/** `Organization/12/invitations/bulk` → `12`; null when the path is not org-scoped. */
function orgIdFromUrl(url: string): string | null {
  return /(?:^|\/)organization\/(\d+)(?:\/|$|\?)/i.exec(url)?.[1] ?? null;
}

function attachAuth(req: HttpRequest<unknown>): HttpRequest<unknown> {
  const token = localStorage.getItem('token');
  const isWrite = req.method === 'POST' || req.method === 'PUT';
  let body = req.body;

  if (
    isWrite &&
    body &&
    !(body instanceof FormData) &&
    typeof body === 'object' &&
    // Spreading an array body into an object would corrupt it (e.g. List<int> endpoints).
    !Array.isArray(body) &&
    !(body as Record<string, unknown>)['idempotencyKey'] &&
    !(body as Record<string, unknown>)['IdempotencyKey']
  ) {
    body = { ...(body as object), idempotencyKey: crypto.randomUUID() };
  }

  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // The backend evaluates org-scoped policies against this header, not the route,
  // so a stale/missing stored id makes an org-scoped call 403 with an empty body
  // even though the caller is a member. Prefer the org the URL is acting on.
  const selectedOrg = req.context.get(PERSONAL_OWNER_SCOPE)
    ? null
    : orgIdFromUrl(req.url) ?? localStorage.getItem('selectedOrganizationId');
  if (selectedOrg) {
    headers['selectedOrganizationId'] = selectedOrg;
  }

  Object.assign(headers, getTestModePreviewHeaders());

  const nativeOrigin = nativeAuthOrigin(
    Capacitor.isNativePlatform(),
    Capacitor.getPlatform(),
  );
  if (nativeOrigin) {
    headers['Origin'] = nativeOrigin;
  }

  return req.clone({
    url: API_BASE + req.url.replace(/^\//, ''),
    body,
    setHeaders: headers,
    withCredentials: true,
  });
}

export function nativeAuthOrigin(
  isNativePlatform: boolean,
  platform: string,
): string | null {
  if (!isNativePlatform) return null;
  switch (platform) {
    case 'android':
      return 'https://app.quiziva.com';
    case 'ios':
      return 'capacitor://app.quiziva.com';
    case 'electron':
      return 'capacitor-electron://-';
    default:
      return null;
  }
}

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authHttp: AuthHttpService,
  router: Router,
) {
  if (refreshing) {
    return refreshDone$.pipe(
      take(1),
      switchMap(() => retryWithNewToken(req, next)),
    );
  }

  refreshing = true;

  return authHttp.refreshToken().pipe(
    switchMap(() => {
      refreshing = false;
      refreshDone$.next();
      return retryWithNewToken(req, next);
    }),
    catchError((err) => {
      refreshing = false;
      refreshDone$.next();
      localStorage.removeItem('refresh_token');
      if (
        isTerminalRefreshCredentialError(err) ||
        localStorage.getItem(REFRESH_SESSION_MARKER) !== '1'
      ) {
        localStorage.removeItem('token');
        localStorage.removeItem(REFRESH_SESSION_MARKER);
        void router.navigate(['/login']);
      }
      return throwError(() => err);
    }),
  );
}

function retryWithNewToken(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const token = localStorage.getItem('token');
  if (!token) {
    return throwError(
      () => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }),
    );
  }
  return next(
    req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    }),
  );
}
